import type { Client, SubmoduleStatus } from "@/lib/types/client";
import { isQuizStepValid } from "@/features/onboarding/AccountConfigQuiz";
import { uploadedLists } from "@/features/onboarding/excelTemplates";

/** Reihenfolge der sechs Wizard-Hauptschritte (src/components/wizard/config.js#STEPS_META). */
export const STEP_IDS = ["willkommen", "stammdaten", "auftraege", "kontokonfiguration", "berichte", "abschluss"] as const;
export type StepId = (typeof STEP_IDS)[number];

/** Reihenfolge der sechs Stammdaten-Submodule (src/components/wizard/config.js#STAMMDATEN_SUB). */
export const STAMMDATEN_SUB_IDS = [
  "firmendaten",
  "benutzerteams",
  "fahrzeuge",
  "auftragsarten",
  "adresstypen",
  "ressourcen"
] as const;
export type StammdatenSubId = (typeof STAMMDATEN_SUB_IDS)[number];

/**
 * Dieselben Submodule für die Mandanten-Stammdatenpflege (S1.3,
 * src/features/tenant/), ohne "firmendaten" — das bleibt an die
 * Onboarding-Identität des Kunden gebunden und wird nicht umgehängt.
 */
export const TENANT_SUB_IDS = STAMMDATEN_SUB_IDS.filter((id) => id !== "firmendaten");

/**
 * Port von checkSubmoduleCompleted() aus src/components/wizard/progress.js —
 * mit korrigierten Feldpfaden (siehe Migrationsplan, Entscheidung 2): das
 * Original prüfte für firmendaten/auftragsarten/adresstypen/ressourcen Felder,
 * die die Formulare nie beschreiben ("Ist immer rot bis explizit bestätigt").
 * Die explizite Bestätigung (stammdatenProgress[sub] === "green") hat
 * weiterhin unverändert Vorrang.
 */
export function checkSubmoduleCompleted(subId: StammdatenSubId, client: Client): SubmoduleStatus {
  if (client.stammdatenProgress?.[subId] === "green") return "green";

  const data = client.data;
  switch (subId) {
    case "firmendaten": {
      const f = data.firmendaten;
      const hasLogo = data.documents.uploadedFiles.some((file) => file.type === "Logo");
      const allRequired = Boolean(
        f?.companyName && f.contactFirstName && f.contactLastName && f.phone && f.email && f.street && f.zip && f.city
      );
      if (allRequired && hasLogo) return "green";
      if (f?.companyName) return "yellow";
      return "red";
    }
    // Pflicht ist die Anlage selbst: ein Benutzer bzw. ein Fahrzeug. Teams und
    // Fahrzeuggruppen sind optional — wer nur sie anlegt, hat begonnen (gelb).
    case "benutzerteams": {
      if ((data.usersTeams?.users?.length || 0) > 0) return "green";
      if ((data.usersTeams?.teams?.length || 0) > 0) return "yellow";
      return "red";
    }
    case "fahrzeuge": {
      if ((data.vehicles?.length || 0) > 0) return "green";
      if ((data.vehicleGroups?.length || 0) > 0) return "yellow";
      return "red";
    }
    case "auftragsarten":
      return (data.orderTypes?.length || 0) > 0 ? "green" : "red";
    case "adresstypen": {
      const a = data.adresstypen;
      const hasAny = (a?.types?.length || 0) > 0 || (a?.addresses?.length || 0) > 0 || Boolean(a?.noAddresses) || uploadedLists(client, "addresses").length > 0;
      return hasAny ? "green" : "red";
    }
    case "ressourcen": {
      const r = data.ressourcen;
      const hasAny = (r?.types?.length || 0) > 0 || (r?.resources?.length || 0) > 0 || Boolean(r?.noResources) || uploadedLists(client, "ressourcen").length > 0;
      return hasAny ? "green" : "red";
    }
    default:
      return "red";
  }
}

/**
 * Fortschritt eines einzelnen Stammdaten-Submoduls (0/50/100), analog zu
 * calculateStepProgress() für "stammdaten" — nur auf ein Submodul statt auf
 * alle sechs zusammen angewandt.
 */
export function calculateSubmoduleProgress(subId: StammdatenSubId, client: Client): number {
  if (!client?.data) return 0;
  if (!client.data.welcomeCompleted) return 0;
  if (client.confirmations?.stammdaten === true) return 100;

  switch (checkSubmoduleCompleted(subId, client)) {
    case "green":
      return 100;
    case "yellow":
      return 50;
    default:
      return 0;
  }
}

/**
 * Port von calculateStepProgress() aus src/components/wizard/progress.js.
 * Rückgabe ist immer 0, 50 oder 100 — es gibt bewusst keine feinere Abstufung.
 */
export function calculateStepProgress(stepId: StepId, client: Client): number {
  if (!client?.data) return 0;
  // Vor dem eigentlichen Start ("Onboarding starten" auf der Welcome-Seite) bleibt jeder
  // Schritt bei 0 – auch wenn admin-seitige Vorbelegungen (z. B. firmendaten.companyName)
  // sonst schon einen Teilfortschritt vortäuschen würden, bevor der Kunde etwas getan hat.
  if (stepId !== "willkommen" && !client.data.welcomeCompleted) return 0;
  if (client.confirmations?.[stepId as keyof Client["confirmations"]] === true) return 100;

  const data = client.data;
  switch (stepId) {
    case "willkommen":
      return data.welcomeCompleted ? 100 : 0;
    case "stammdaten": {
      const statuses = STAMMDATEN_SUB_IDS.map((sub) => checkSubmoduleCompleted(sub, client));
      if (statuses.every((status) => status === "green")) return 100;
      if (statuses.every((status) => status === "red")) return 0;
      return 50;
    }
    case "auftraege": {
      const wf = data.workflows;
      const hasContent = Boolean(wf?.workflowDescription || wf?.files?.length || wf?.attributes?.length);
      return hasContent ? 100 : 0;
    }
    case "kontokonfiguration": {
      const ac = data.accountConfig;
      const allAnswered = Array.from({ length: 10 }, (_, idx) => idx).every((idx) => isQuizStepValid(idx, ac));
      if (allAnswered) return 100;
      return ac?.interacted ? 50 : 0;
    }
    case "berichte": {
      const rep = data.reports;
      const hasContent = Boolean(
        rep?.standardReports?.length || rep?.customReportsNotes || rep?.hasCustomRequest || rep?.noReports
      );
      return hasContent ? 100 : 0;
    }
    case "abschluss":
      return client.status === "Final gesperrt" || data.finish?.done ? 100 : 0;
    default:
      return 0;
  }
}
